import { useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock,
  Cylinder,
  FileText,
  Loader2,
  MapPin,
  Plus,
  ReceiptText,
  RotateCw,
  ShoppingCart,
  Sparkles,
  Truck,
  Wrench,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatUsd, initials } from '@/lib/format';
import { useSession } from '@/lib/auth';
import { useCustomer } from '@/hooks/useCustomer';
import { useCustomerFeatures } from '@/hooks/useCustomerFeatures';
import { useCylinderInventory } from '@/hooks/useCylinders';
import { useAccountSummary } from '@/hooks/useAccountSummary';
import { useCatalog } from '@/hooks/useCatalog';
import { useOrderHistory } from '@/hooks/useOrderHistory';
import { useActivity } from '@/hooks/useActivity';
import { useReorder, type ReorderSummary } from '@/hooks/useReorder';
import { reorderMessage } from '@/components/orders/ReorderButton';
import { useCart } from '@/hooks/useCart';
import { OrderAgainCard } from '@/components/home/OrderAgainCard';
import { EquipmentHomeCard } from '@/components/home/EquipmentHomeCard';
import { ProductImage } from '@/components/catalog/ProductImage';
import { ServiceTicketsCard } from '@/components/home/ServiceTicketsCard';
import { displayName, type CatalogItem } from '@/types/domain';
import type { ShellOutletCtx } from '@/lib/shell-context';

/**
 * /home — the customer dashboard.
 *
 * Live today:
 *  - AR snapshot from `orders.v_account_summary`
 *  - Most ordered from `useOrderHistory`
 *  - Service tickets list + create via `orders.service_requests`
 *
 * Stubs (with shape preview so customers see what's coming):
 *  - Recent orders — `useOrders` hook not built yet
 *  - Equipment + cylinders — `iuyihatkeprrjmdlltjb` cross-Supabase pending
 */
export default function Home() {
  const navigate = useNavigate();
  const { selectedLocation } = useOutletContext<ShellOutletCtx>();
  const { session } = useSession();
  const { data: ctx } = useCustomer();
  const { data: features } = useCustomerFeatures();
  const { data: summary, isLoading: summaryLoading } = useAccountSummary();
  const { data: items = [], isLoading: catalogLoading } = useCatalog();
  const { data: history, isLoading: historyLoading } = useOrderHistory();
  const { data: activity } = useActivity();
  const { reorderById } = useReorder();
  const { cart, adjustQty } = useCart();

  // Most recent portal order (order-sourced activity rows carry order_lines we
  // can rebuild). Powers the "Reorder last order" quick action — only shown
  // when the customer has at least one such order.
  const lastOrderId = useMemo(() => {
    const rows = activity ?? [];
    // useActivity already sorts by activity_date desc.
    const last = rows.find((r) => r.source === 'order' && Boolean(r.rowId));
    return last?.rowId ?? null;
  }, [activity]);

  const [reorderBusy, setReorderBusy] = useState(false);
  const [reorderNotice, setReorderNotice] = useState<{
    tone: 'success' | 'warning';
    text: string;
  } | null>(null);

  async function reorderLast() {
    if (!lastOrderId || reorderBusy) return;
    setReorderBusy(true);
    try {
      const summary: ReorderSummary | null = await reorderById(lastOrderId);
      if (summary && summary.added > 0) {
        navigate('/cart');
      } else {
        setReorderNotice({ tone: 'warning', text: reorderMessage(summary) });
      }
    } finally {
      setReorderBusy(false);
    }
  }

  const fullName =
    ctx?.user.fullName ??
    session?.user?.email?.split('@')[0] ??
    'there';
  const firstName = fullName.split(' ')[0] ?? fullName;

  // Dismissible "New here? Take the tour" card → /guide. Dismissal persists in
  // localStorage so returning users don't keep seeing it.
  const [guideDismissed, setGuideDismissed] = useState(
    () => localStorage.getItem('brix_guide_dismissed') === '1',
  );
  function dismissGuide() {
    localStorage.setItem('brix_guide_dismissed', '1');
    setGuideDismissed(true);
  }

  // Top 4 most-ordered items joined back to the catalog so we can show
  // image + price + quick-add. Skips items that have rolled off the
  // catalog since the order was placed.
  const mostOrdered = useMemo(() => {
    const freq = history?.byCatalogItemId;
    if (!freq || freq.size === 0) return [] as CatalogItem[];
    const byId = new Map(items.map((i) => [i.id, i] as const));
    return [...freq.values()]
      .sort((a, b) => b.orderCount - a.orderCount)
      .map((f) => byId.get(f.catalogItemId))
      .filter((x): x is CatalogItem => Boolean(x))
      .slice(0, 4);
  }, [items, history]);

  const hasHistory = mostOrdered.length > 0;

  // Past-due alert lives in AppShell's PastDueModal now (true blocking
  // popup, not an inline banner). The customer never sees overdue copy
  // on /home anymore — it surfaces as a modal that locks ordering.

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-20 md:pb-10">
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-6 pb-12 space-y-6">

        {/* Hero — name + optional avatar. By default we render initials in a
            red circle; once a user uploads a profile picture (TODO: Account
            page upload → Supabase Storage), that pic replaces the initials. */}
        <header className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold bg-red-600 text-white shrink-0 relative overflow-hidden shadow-card"
            aria-hidden
          >
            <span className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />
            <span className="relative">{initials(fullName)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-brix-ink leading-tight">
              {greeting()}, {firstName}.
            </h1>
            <p className="text-sm text-brix-muted truncate flex items-center gap-1.5 mt-0.5">
              {ctx?.customer.name && <span className="truncate">{ctx.customer.name}</span>}
              {ctx?.customer.name && selectedLocation && <span>·</span>}
              {selectedLocation && (
                <span className="inline-flex items-center gap-1 text-brix-primary">
                  <MapPin size={12} /> {selectedLocation}
                </span>
              )}
            </p>
          </div>
        </header>

        {/* New-here tour card — dismissible, links to the Getting Started guide. */}
        {!guideDismissed && (
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-brix-primary/30 bg-brix-primary-tint/50 px-5 py-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="w-10 h-10 rounded-xl bg-brix-primary text-white flex items-center justify-center shrink-0">
                <BookOpen size={18} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-brix-ink">New here? Take the tour.</p>
                <p className="text-xs text-brix-muted mt-0.5">
                  A quick walkthrough of ordering, billing, and everything this portal does.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/guide">
                <Button type="button" size="sm">
                  Take the tour <ArrowRight size={13} />
                </Button>
              </Link>
              <button
                type="button"
                onClick={dismissGuide}
                aria-label="Dismiss"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-brix-muted hover:bg-brix-primary-tint cursor-pointer transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* AR snapshot */}
        <Card>
          {summaryLoading ? (
            <div className="flex items-center gap-2 text-sm text-brix-muted">
              <Loader2 size={14} className="animate-spin" /> Loading account summary…
            </div>
          ) : !summary ? (
            <p className="text-sm text-brix-muted">
              Account summary unavailable.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <FramedStat label="Open balance" value={formatUsd(summary.openBalance)} />
              <FramedStat
                label="Past due"
                value={formatUsd(summary.pastDueBalance)}
                tone={summary.pastDueBalance > 0 ? 'warning' : 'muted'}
              />
              <FramedStat
                label="Open invoices"
                value={summary.openInvoiceCount.toString()}
              />
            </div>
          )}
          {summary?.onCreditHold && (
            <div className="mt-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-brix-primary-tint">
              <AlertCircle size={14} className="text-brix-warning mt-0.5 shrink-0" />
              <p className="text-[11px] leading-relaxed text-brix-ink">
                Your account is currently on credit hold. Settle past-due invoices to
                resume ordering, or call support if this looks wrong.
              </p>
            </div>
          )}
        </Card>

        {/* Quick actions — each icon sits inside a light circular halo so the
            row reads as a row of "buttons" rather than dense pills. */}
        <div className="flex flex-wrap gap-3">
          <QuickAction icon={<ShoppingCart size={18} />} label="Place an order" tone="primary" onClick={() => navigate('/order')} />
          {lastOrderId && (
            <QuickAction
              icon={
                reorderBusy ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <RotateCw size={18} />
                )
              }
              label="Reorder last order"
              onClick={reorderLast}
              disabled={reorderBusy}
            />
          )}
          <QuickAction icon={<Sparkles size={18} />} label="My Collection" onClick={() => navigate('/collection')} />
          <QuickAction icon={<ReceiptText size={18} />} label="Billing" onClick={() => navigate('/invoices')} />
          <QuickAction icon={<FileText size={18} />} label="Account" onClick={() => navigate('/settings/profile')} />
        </div>

        {reorderNotice && (
          <div
            role="status"
            className={cn(
              'flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium',
              reorderNotice.tone === 'success'
                ? 'border-brix-primary/30 bg-brix-primary-tint/60 text-brix-primary'
                : 'border-amber-200 bg-amber-50 text-amber-800',
            )}
          >
            {reorderNotice.tone === 'success' ? (
              <CheckCircle2 size={15} className="shrink-0" />
            ) : (
              <X size={15} className="shrink-0" />
            )}
            <span>{reorderNotice.text}</span>
          </div>
        )}

        {/* Order Again — top items by frequency, one-tap add to cart. */}
        <OrderAgainCard />

        {/* 2-col grid on md+ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Most ordered */}
          <Card>
            <SectionHeading icon={<Sparkles size={16} />}>Most ordered</SectionHeading>
            {catalogLoading || historyLoading ? (
              <div className="flex items-center gap-2 text-sm text-brix-muted">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : !hasHistory ? (
              <EmptyState
                title="No order history yet."
                body="Your top items show up here for one-tap reordering once you start placing orders."
                ctaLabel="Browse the catalog"
                onCta={() => navigate('/order')}
              />
            ) : (
              <ul className="space-y-2">
                {mostOrdered.map((item) => (
                  <MostOrderedRow
                    key={item.id}
                    item={item}
                    inCart={cart[item.id] ?? 0}
                    onAdd={() => adjustQty(item.id, 1)}
                  />
                ))}
                <li>
                  <button
                    type="button"
                    onClick={() => navigate('/collection')}
                    className="text-xs font-semibold text-brix-primary hover:underline inline-flex items-center gap-1 pt-1"
                  >
                    See your full collection <ArrowRight size={12} />
                  </button>
                </li>
              </ul>
            )}
          </Card>

          {/* Service tickets */}
          {features?.serviceRequests !== false && (
            <Card>
              <SectionHeading icon={<Wrench size={16} />}>Service tickets</SectionHeading>
              {!ctx ? (
                <div className="flex items-center gap-2 text-sm text-brix-muted">
                  <Loader2 size={14} className="animate-spin" /> Loading…
                </div>
              ) : (
                <ServiceTicketsCard
                  customerId={ctx.customer.id}
                  defaultLocationId={ctx.user.defaultLocationId}
                />
              )}
            </Card>
          )}

          {/* Recent orders — stub today (0 orders) */}
          <Card>
            <SectionHeading icon={<Clock size={16} />}>Recent orders</SectionHeading>
            <EmptyState
              title="No orders yet."
              body="Your three most recent orders will live here with status (submitted → out for delivery → delivered) and a one-tap reorder button."
              ctaLabel="Place your first order"
              onCta={() => navigate('/order')}
            />
          </Card>

          {/* Cylinder inventory mini-card — live counts. Hidden when the
              feature flag is off for this customer (e.g. syrup-only). */}
          {features?.cylinderInventory !== false && (
            <CylinderHomeCard />
          )}

          {/* Equipment on rent — pulled nightly from the
              Equipment_Leasing_Rental Supabase project into our local
              ops.equipment_assets mirror. Self-hides for customers with
              no rentals on file. */}
          <EquipmentHomeCard />
        </div>

        {/* Footer microbar */}
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-brix-muted pt-2">
          <Truck size={12} /> Standard delivery on confirmed orders inside 48 hours.
        </div>
      </div>
    </div>
  );
}

/* ------------ presentational helpers ------------- */

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-brix-border bg-brix-surface p-5 space-y-4',
        // Morph: layered shadows give a soft "lifted" feel; hover bumps the lift.
        'shadow-card-hover hover:shadow-card-deep hover:-translate-y-0.5 transition-all duration-300',
      )}
    >
      {children}
    </div>
  );
}

function CylinderHomeCard() {
  const navigate = useNavigate();
  const { data: rows, isLoading } = useCylinderInventory();
  const inUse = (rows ?? []).filter(
    (r) => r.currentBalance > 0 || r.deliveriesSince > 0 || r.pickupsSince > 0,
  );
  const totalTanks = inUse.reduce((a, r) => a + r.currentBalance, 0);
  const totalRental = inUse.reduce((a, r) => a + r.estimatedCharge, 0);

  return (
    <button
      type="button"
      onClick={() => navigate('/cylinders')}
      className={cn(
        'group block w-full text-left rounded-2xl border-2 border-brix-primary-deep bg-brix-surface p-5 space-y-4 cursor-pointer',
        'shadow-card-hover hover:shadow-card-deep hover:-translate-y-0.5 transition-all duration-300',
      )}
    >
      <div className="flex items-center justify-between">
        <SectionHeading icon={<Cylinder size={16} />}>Cylinders on rent</SectionHeading>
        <ArrowRight
          size={15}
          className="text-brix-primary transition-transform group-hover:translate-x-0.5"
        />
      </div>
      {isLoading ? (
        <div className="text-sm text-slate-900 tracking-tight inline-flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      ) : inUse.length === 0 ? (
        <p className="text-sm text-slate-900 tracking-tight leading-relaxed">
          No cylinders on rent. If you should be seeing tanks here, let us
          know.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] tracking-tight text-slate-900 font-bold">
              Total tanks
            </div>
            <div
              className="font-mono text-3xl font-bold text-slate-900 tracking-tight leading-none mt-1"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {totalTanks}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-tight text-slate-900 font-bold">
              Est. monthly rent
            </div>
            <div
              className="font-mono text-3xl font-bold text-brix-primary tracking-tight leading-none mt-1"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatUsd(totalRental)}
            </div>
          </div>
        </div>
      )}
      <div className="text-[11px] text-brix-primary font-semibold tracking-tight inline-flex items-center gap-1">
        Click for details <ArrowRight size={11} />
      </div>
    </button>
  );
}

function SectionHeading({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-brix-primary">{icon}</span>
      <h2 className="font-display text-base font-semibold text-brix-ink">{children}</h2>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  tone = 'secondary',
  onClick,
  disabled = false,
}: {
  icon: ReactNode;
  label: string;
  tone?: 'primary' | 'secondary';
  onClick: () => void;
  disabled?: boolean;
}) {
  const isPrimary = tone === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group flex items-center gap-2.5 pr-4 py-1.5 pl-1.5 rounded-full',
        'border border-brix-border bg-brix-surface',
        'shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 cursor-pointer',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0',
      )}
    >
      <span
        className={cn(
          'w-9 h-9 rounded-full flex items-center justify-center shrink-0',
          'transition-colors duration-200',
          isPrimary
            ? 'bg-brix-primary text-white shadow-[0_0_0_4px_rgba(31,78,121,0.15)]'
            : 'bg-brix-primary-tint text-brix-primary shadow-[0_0_0_4px_rgba(31,78,121,0.07)] group-hover:shadow-[0_0_0_4px_rgba(31,78,121,0.14)]',
        )}
      >
        {icon}
      </span>
      <span className={cn('text-sm font-semibold', isPrimary ? 'text-brix-primary' : 'text-brix-ink')}>
        {label}
      </span>
    </button>
  );
}

function FramedStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'warning' | 'muted';
}) {
  const valueColor =
    tone === 'success'
      ? 'text-brix-success'
      : tone === 'warning'
        ? 'text-brix-warning'
        : tone === 'muted'
          ? 'text-brix-muted'
          : 'text-brix-ink';
  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface-alt/60 px-4 py-3 shadow-card flex flex-col h-full">
      <p className="text-sm tracking-tight text-brix-muted font-semibold">
        {label}
      </p>
      <p className={cn('mt-1.5 font-bold text-2xl leading-tight tracking-tight', valueColor)}>
        {value}
      </p>
    </div>
  );
}

function EmptyState({
  title,
  body,
  ctaLabel,
  onCta,
}: {
  title: string;
  body: string;
  ctaLabel: string;
  onCta: () => void;
}) {
  return (
    <div className="text-center py-3">
      <p className="text-sm font-semibold text-brix-ink">{title}</p>
      <p className="text-xs text-brix-muted mt-1 leading-relaxed max-w-md mx-auto">{body}</p>
      <Button type="button" size="sm" variant="secondary" onClick={onCta} className="mt-3">
        {ctaLabel}
      </Button>
    </div>
  );
}

function MostOrderedRow({
  item,
  inCart,
  onAdd,
}: {
  item: CatalogItem;
  inCart: number;
  onAdd: () => void;
}) {
  const name = displayName(item);
  return (
    <li className="flex items-center gap-3 py-1.5">
      <ProductImage
        imageUrl={item.imageUrl}
        accent={item.accentColor ?? '#6A7780'}
        category={item.category}
        alt={name}
        size="sm"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-brix-ink truncate">{name}</p>
        <p className="text-[11px] text-brix-muted truncate">
          {item.category}
          {item.sku ? ` · ${item.sku}` : ''}
        </p>
      </div>
      <Button type="button" size="sm" variant={inCart > 0 ? 'secondary' : 'ghost'} onClick={onAdd}>
        <Plus size={12} /> {inCart > 0 ? `In cart · ${inCart}` : 'Add'}
      </Button>
    </li>
  );
}
